/**
 * live/narrative.ts — la cronaca del live, riga per riga.
 *
 * Il feed grezzo diceva solo «Punto · ospite»: il tipo d'azione e chi l'ha fatta,
 * senza il resto. Ma ogni evento porta con sé lo **stato precedente** completo
 * (punteggio, set, parziali, chi batteva, timeout usati), quindi tutto quello che
 * serve al racconto c'è già: qui si ricava e si scrive.
 *
 * Da un solo evento si ottengono: il punteggio **dopo** il punto, il set e se è
 * tiebreak, chi batteva prima, e le note che danno il senso della partita —
 * palla set annullata, set chiuso, palla match, allungo, sorpasso.
 *
 * Le regole non si inventano: `isSetPoint`/`isMatchPoint` sono le stesse che usa
 * il tabellone, applicate allo stato **precedente** al punto.
 */
import type { MatchState } from './state'
import { isMatchPoint, isSetPoint, isSetWon } from './score-points'
import { liveEventLabel } from './event-labels'

/** L'evento come lo serve `/api/live/[sessionKey]/events`. */
export interface NarrativeEventRow {
  id: number
  revision: number
  type: string
  team: string | null
  payload: Record<string, unknown>
  created_at: string
}

/**
 * I tipi che valgono un punto. Solo `point`: il gesto tecnico (muro, attacco,
 * difesa…) è la descrizione di ciò che è successo, il punto lo conferma
 * l'operatore col pulsante. Contarli entrambi gonfierebbe i parziali.
 */
const POINT_TYPES = new Set(['point'])

type Side = 'home' | 'away'

export interface NarrativeScore {
  home: number
  away: number
}

export interface NarrativeEntry {
  id: number
  type: string
  /** Etichetta dell'azione («Punto», «Muro», «Time-out»). */
  label: string
  side: Side | null
  set: number
  isTiebreak: boolean
  /** Punteggio **dopo** l'evento (per i punti) o al momento dell'evento. */
  score: NarrativeScore
  /** Chi batteva quando l'evento è avvenuto. */
  serving: Side | null
  at: string
  /** Note derivate: allungo, palla set annullata, set chiuso… */
  notes: string[]
  /** Il punto ha chiuso il set (parziale registrato in `setHistory`). */
  closesSet: boolean
}

export interface NarrativeSet {
  set: number
  /** Dal più recente al più vecchio, come si legge un live. */
  entries: NarrativeEntry[]
  /** Parziale finale: dai parziali registrati, o dall'ultimo punto se in corso. */
  finalScore: NarrativeScore | null
  winner: Side | null
  inProgress: boolean
  timeouts: { home: number; away: number }
}

function sideOf(team: string | null): Side | null {
  return team === 'home' || team === 'away' ? team : null
}

function prevStateOf(row: NarrativeEventRow): MatchState | null {
  const prev = row.payload?.previousState
  if (!prev || typeof prev !== 'object') return null
  const state = prev as MatchState
  return state.currentScore && typeof state.currentSet === 'number' ? state : null
}

/** «3-2» / «25-20»: sempre casa-ospiti, come sul tabellone. */
function scoreText(score: NarrativeScore): string {
  return `${score.home}-${score.away}`
}

/**
 * Costruisce la cronaca completa, raggruppata per set.
 *
 * `setHistory` è l'elenco dei parziali dei set **chiusi** (dallo stato): serve a
 * dire chi ha vinto un set e con che punteggio, e a distinguere il set in corso.
 */
export function buildLiveNarrative(
  events: NarrativeEventRow[],
  setHistory: Array<{ home: number; away: number }> = [],
): NarrativeSet[] {
  const bySet = new Map<number, NarrativeEntry[]>()
  const timeouts = new Map<number, { home: number; away: number }>()
  // Ultimo punto per set: serve a chiudere il parziale del set in corso.
  const lastPoint = new Map<number, NarrativeScore>()
  // Serie in corso di punti della stessa squadra, per le note «allungo».
  let runSide: Side | null = null
  let runLength = 0
  let runSet = -1

  for (const row of events) {
    const prev = prevStateOf(row)
    const set = prev ? prev.currentSet : bySet.size + 1
    const isTiebreak = Boolean(prev?.isTiebreak)
    const side = sideOf(row.team)
    const isPoint = POINT_TYPES.has(row.type)
    const notes: string[] = []

    let score: NarrativeScore
    let closesSet = false

    if (isPoint && prev) {
      const before: NarrativeScore = { home: prev.currentScore.home, away: prev.currentScore.away }
      score = { home: before.home, away: before.away }
      if (side) score[side] += 1

      const opponent: Side | null = side ? (side === 'home' ? 'away' : 'home') : null
      const beforeDiff = before.home - before.away
      const afterDiff = score.home - score.away

      // Palla set annullata: chi ha fatto il punto NON era in vantaggio di set,
      // ma l'avversario sì (stessa regola del pallino «SP» sul tabellone).
      if (side && opponent && isSetPoint(prev, opponent) && !isSetPoint(prev, side)) {
        notes.push('annulla una palla set')
      }
      // Palla match annullata, sul 2-2 nei set.
      if (side && opponent && isMatchPoint(prev, opponent) && !isMatchPoint(prev, side)) {
        notes.push('annulla una palla match')
      }
      // Set chiuso da questo punto (parziale registrato subito dopo nel setHistory).
      if (side && isSetWon(score[side], score[side === 'home' ? 'away' : 'home'], set)) {
        closesSet = true
        notes.push(`chiude il ${set}° set ${scoreText(score)}`)
        // Palla match convertita: stessa regola del tabellone, applicata allo stato
        // precedente al punto.
        if (isMatchPoint(prev, side)) notes.push('chiude la partita')
      }
      // Sorpasso: con questo punto si passa da pari/sotto a davanti (in qualsiasi
      // direzione; in questa partita è capitato 22 volte).
      if (side && beforeDiff <= 0 && afterDiff > 0) notes.push('sorpasso')
      if (side && beforeDiff >= 0 && afterDiff < 0) notes.push('sorpasso')

      // Serie: quanti punti di fila ha fatto la stessa squadra, questo incluso.
      if (side === runSide && set === runSet) runLength += 1
      else {
        runSide = side
        runSet = set
        runLength = side ? 1 : 0
      }
      if (runLength === 3 || runLength === 5 || runLength === 8) {
        notes.push(runLength === 3 ? 'tre di fila' : `allungo di ${runLength}`)
      }
      lastPoint.set(set, score)
    } else {
      // Timeout, cambi di stato, note: il punteggio è quello al momento.
      score = prev
        ? { home: prev.currentScore.home, away: prev.currentScore.away }
        : { home: 0, away: 0 }
      if (prev) lastPoint.set(set, score)
      if (row.type.startsWith('timeout')) {
        const t = timeouts.get(set) ?? { home: 0, away: 0 }
        if (side) t[side] += 1
        timeouts.set(set, t)
        if (side) notes.push(`sul ${scoreText(score)}`)
      }
    }

    if (!bySet.has(set)) bySet.set(set, [])
    bySet.get(set)!.push({
      id: row.id,
      type: row.type,
      label: liveEventLabel(row.type),
      side,
      set,
      isTiebreak,
      score,
      serving: prev?.serviceTeam ?? null,
      at: row.created_at,
      notes,
      closesSet,
    })
  }

  return [...bySet.entries()]
    .sort((a, b) => b[0] - a[0]) // set dal più recente
    .map(([set, entries]) => {
      const registered = setHistory[set - 1]
      const last = lastPoint.get(set) ?? null
      const finalScore = registered ?? last
      const winner: Side | null = registered
        ? registered.home > registered.away
          ? 'home'
          : 'away'
        : null
      return {
        set,
        entries: [...entries].reverse(), // dentro il set, dal più recente
        finalScore,
        winner,
        inProgress: !registered,
        timeouts: timeouts.get(set) ?? { home: 0, away: 0 },
      }
    })
}
